import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "./_shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FlaskConical, Sparkles } from "lucide-react";

export default function Simulator() {
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState(null);
  const [priceChange, setPriceChange] = useState(0);
  const [marketing, setMarketing] = useState(0);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (r.data.length) setPid(r.data[0].id);
    });
  }, []);

  const run = async () => {
    if (!pid) return;
    setRunning(true);
    try {
      const r = await api.post("/simulator", { product_id: pid, price_change_pct: priceChange, marketing_boost_pct: marketing });
      setResult(r.data);
    } finally { setRunning(false); }
  };

  return (
    <Page
      title="Simulador de impacto"
      subtitle="Teste cenários de preço e marketing antes de aplicar. Veja o ROI projetado."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-xl border border-border bg-card space-y-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><FlaskConical className="w-4 h-4 text-primary" /> Configurar cenário</div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Produto</label>
            <Select value={pid || ""} onValueChange={setPid}>
              <SelectTrigger data-testid="sim-product-select" className="mt-2"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground mb-3">
              <span>Variação de preço</span>
              <span className="text-primary font-mono">{priceChange > 0 ? "+" : ""}{priceChange}%</span>
            </div>
            <Slider data-testid="sim-price-slider" min={-30} max={30} step={1} value={[priceChange]} onValueChange={(v) => setPriceChange(v[0])} />
          </div>

          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground mb-3">
              <span>Boost de marketing</span>
              <span className="text-primary font-mono">+{marketing}%</span>
            </div>
            <Slider data-testid="sim-marketing-slider" min={0} max={100} step={5} value={[marketing]} onValueChange={(v) => setMarketing(v[0])} />
          </div>

          <Button data-testid="sim-run-btn" disabled={!pid || running} onClick={run} className="w-full bg-primary hover:bg-primary/90">
            <Sparkles className="w-4 h-4 mr-2" /> {running ? "Simulando..." : "Simular cenário"}
          </Button>
        </div>

        <div className="p-6 rounded-xl border border-border bg-card">
          {!result ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-6">
              Configure um cenário ao lado e clique em "Simular".<br/>
              A IA aplicará elasticidade de demanda e estimará o impacto.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Resultado · {result.product}</div>
              <Compare label="Demanda 30d" base={`${result.baseline.demand_30d} un`} scen={`${result.scenario.demand_30d} un`} delta={result.delta.demand_pct} />
              <Compare label="Receita" base={formatBRL(result.baseline.revenue)} scen={formatBRL(result.scenario.revenue)} delta={result.delta.revenue_pct} />
              <Compare label="Lucro" base={formatBRL(result.baseline.profit)} scen={formatBRL(result.scenario.profit)} delta={result.delta.profit_pct} />
              <Compare label="Preço unitário" base={formatBRL(result.baseline.price)} scen={formatBRL(result.scenario.price)} delta={null} />
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

const Compare = ({ label, base, scen, delta }) => {
  const positive = delta != null && delta > 0;
  const negative = delta != null && delta < 0;
  return (
    <div className="grid grid-cols-3 gap-3 p-4 rounded-lg border border-border bg-secondary/40 items-baseline">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-right">
        <div className="text-xs text-muted-foreground">base</div>
        <div className="font-mono text-sm">{base}</div>
      </div>
      <div className="text-right">
        <div className="text-xs text-muted-foreground">cenário</div>
        <div className={`font-mono text-sm font-semibold ${positive ? "text-emerald-400" : negative ? "text-red-400" : "text-foreground"}`}>{scen}</div>
        {delta != null && <div className={`text-xs ${positive ? "text-emerald-400" : negative ? "text-red-400" : "text-muted-foreground"}`}>{delta > 0 ? "+" : ""}{delta}%</div>}
      </div>
    </div>
  );
};
